import { utcOffsetMinutes } from '../autoScale/AutoScale';

export interface IMetricsSummary {
  peak24h: number;
  peak30d: number;
  avgPlayers24h: number;
  playerHours24h: number;
  /** Percentage of sampled minutes with zero players anywhere */
  idlePercent24h: number;
  estimatedCost24h: number;
  estimatedCost30d: number;
  logins24h: number;
  logins7d: number;
  /** Low values mean the series has not been collecting long enough to trust */
  sampleCount24h: number;
}

export interface IMetricsPoint {
  at: string;
  total: number;
  byRegion: Record<string, number>;
}

export interface IMetricsTimeseries {
  hours: number;
  bucketMinutes: number;
  regions: { id: string; label: string }[];
  points: IMetricsPoint[];
}

export interface IMetricsDailyRow {
  day: string;
  regionId: string;
  regionLabel: string;
  peakPlayers: number;
  peakAt: string;
  avgPlayers: number;
  playerMinutes: number;
  instanceMinutes: number;
  emptyMinutes: number;
  serverCount: number;
  estimatedCost: number;
}

export interface IMetricsDailyGlobal {
  day: string;
  peakPlayers: number;
  playerMinutes: number;
  instanceMinutes: number;
  emptyMinutes: number;
  estimatedCost: number;
  idlePercent: number;
}

export interface IMetricsDaily {
  days: number;
  rows: IMetricsDailyRow[];
  global: IMetricsDailyGlobal[];
}

export interface IMetricsLiveServer {
  serverName: string;
  region: string;
  regionId: string;
  ip: string;
  status: string;
  autoScaled: boolean;
  instanceType: string;
  inBrowser: boolean;
  players: number;
  maxPlayers: number;
  map: string;
  playerList: { name: string; score: number; ping: number }[];
}

export interface IMetricsLive {
  totalPlayers: number;
  serversUp: number;
  live: IMetricsLiveServer[];
}

export interface IMetricsBandwidthInstance {
  id: string;
  label: string;
  ip: string;
  region: string;
  plan: string;
  days: { day: string; incomingGb: number; outgoingGb: number }[];
  totalOutgoingGb: number;
}

/** Stable-ish colour per region so a region keeps its colour across charts */
export const REGION_COLOURS = [
  '#4f9dff', '#6fdc8c', '#ffb454', '#ff7a90', '#b28dff',
  '#5ad8e6', '#ffd866', '#ff9f6e', '#8fd66f', '#e68ddb',
];

export const colourForRegion = (regionId: string, index: number): string =>
  REGION_COLOURS[index % REGION_COLOURS.length];

export const formatHour = (iso: string): string => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const formatDay = (value: string): string => {
  const d = new Date(value);
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

export interface IMetricsPeakHour {
  hourUtc: number;
  avgPlayers: number;
  peakPlayers: number;
  samples: number;
}

export interface IMetricsPeakHourRegion {
  regionId: string;
  label: string;
  tz: string | null;
  hours: IMetricsPeakHour[];
  busiestHourUtc: number | null;
  busiestAvgPlayers: number;
}

export interface IMetricsSpawnStats {
  spawns: number;
  medianBootSeconds: number | null;
  medianTimeToFirstPlayerSeconds: number | null;
  everUsed: number;
  neverUsed: number;
  neverUsedPercent: number;
  medianLifetimeMinutes: number | null;
}

export interface IMetricsSpawnRow {
  regionId: string;
  label: string;
  role: string;
  trigger: string;
  instanceType: string;
  spawnedAt: string;
  bootSeconds: number | null;
  timeToFirstPlayerSeconds: number | null;
  lifetimeMinutes: number | null;
  peakPlayers: number;
  neverUsed: boolean;
  reapReason: string | null;
}

export interface IMetricsSpawns {
  days: number;
  overall: IMetricsSpawnStats;
  regions: (IMetricsSpawnStats & { regionId: string; label: string })[];
  recent: IMetricsSpawnRow[];
}

/** 95 -> "1m 35s" */
export const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

/**
 * Converts an hour-of-day from UTC into a region's local hour.
 * Zones offset by a fraction of an hour (India, parts of Australia) are floored
 * to the hour, which is fine for a 24 bucket histogram.
 */
export const utcHourToLocal = (hourUtc: number, tz: string | null): number => {
  if (!tz) return hourUtc;
  const offsetHours = utcOffsetMinutes(tz) / 60;
  return ((Math.floor(hourUtc + offsetHours) % 24) + 24) % 24;
};
